import type { CanvasLayer, FrameContext } from "./canvasLayer";

/**
 * Renders a radial iris-wipe transition when moving between levels.
 *
 * Two phases:
 *  - Closing: black mask expands inward (circle shrinks to zero) over the old board.
 *  - Opening: black mask retracts outward (circle grows to full) over the new board.
 *
 * The engine signals that a transition should occur; the UI drives timing and rendering.
 */
export class LevelTransitionOverlay implements CanvasLayer {
	private state: "inactive" | "closing" | "opening" = "inactive";
	private elapsed = 0;
	private centerX = 0;
	private centerY = 0;
	private maxRadius = 0;

	static readonly CLOSE_DURATION_MS = 400;
	static readonly OPEN_DURATION_MS = 400;

	startClosing(centerX: number, centerY: number, viewportWidth: number, viewportHeight: number): void {
		this.state = "closing";
		this.elapsed = 0;
		this.centerX = centerX;
		this.centerY = centerY;
		this.maxRadius = maxRadiusForViewport(centerX, centerY, viewportWidth, viewportHeight);
	}

	startOpening(centerX: number, centerY: number, viewportWidth: number, viewportHeight: number): void {
		this.state = "opening";
		this.elapsed = 0;
		this.centerX = centerX;
		this.centerY = centerY;
		this.maxRadius = maxRadiusForViewport(centerX, centerY, viewportWidth, viewportHeight);
	}

	passTime(ms: number): void {
		if (this.state === "inactive") return;
		this.elapsed += ms;
	}

	isComplete(): boolean {
		if (this.state === "inactive") return true;
		const duration =
			this.state === "closing" ? LevelTransitionOverlay.CLOSE_DURATION_MS : LevelTransitionOverlay.OPEN_DURATION_MS;
		return this.elapsed >= duration;
	}

	isActive(): boolean {
		return this.state !== "inactive";
	}

	stop(): void {
		this.state = "inactive";
	}

	clear(): void {
		this.stop();
	}

	render(ctx: CanvasRenderingContext2D, frame: FrameContext): void {
		if (this.state === "inactive") return;
		const viewportWidth = frame.viewportWidth;
		const viewportHeight = frame.viewportHeight;

		const duration =
			this.state === "closing" ? LevelTransitionOverlay.CLOSE_DURATION_MS : LevelTransitionOverlay.OPEN_DURATION_MS;
		const t = Math.min(1, this.elapsed / duration);

		// Ease-in-out for a smooth feel
		const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

		let radius: number;
		if (this.state === "closing") {
			radius = this.maxRadius * (1 - eased);
		} else {
			radius = this.maxRadius * eased;
		}

		// Draw black with a circular hole (even-odd fill)
		ctx.save();
		ctx.beginPath();
		ctx.rect(0, 0, viewportWidth, viewportHeight);
		ctx.arc(this.centerX, this.centerY, Math.max(0, radius), 0, Math.PI * 2, true);
		ctx.fillStyle = "#000";
		ctx.fill("evenodd");
		ctx.restore();
	}
}

/** Distance from (cx, cy) to the farthest corner of the viewport. */
function maxRadiusForViewport(cx: number, cy: number, w: number, h: number): number {
	const dx = Math.max(cx, w - cx);
	const dy = Math.max(cy, h - cy);
	return Math.sqrt(dx * dx + dy * dy);
}
